
/**
 * Auto-backup service to ensure data persistence across app restarts
 */

import { largeDataStorage } from './largeDataStorage';

class AutoBackupService {
  private backupInterval: NodeJS.Timeout | null = null;
  private readonly BACKUP_INTERVAL = 2 * 60 * 1000; // 2 minutes
  private readonly MAX_BACKUPS = 5;

  start(): void {
    console.log('🛡️ Starting auto-backup service...');
    
    // Initial backup validation on startup
    this.validateAndRestore();
    
    // Start periodic backups
    this.backupInterval = setInterval(() => {
      this.createEmergencyBackup();
    }, this.BACKUP_INTERVAL);
    
    // Backup before page unload
    window.addEventListener('beforeunload', () => {
      this.createEmergencyBackup();
    });
  }

  stop(): void {
    if (this.backupInterval) {
      clearInterval(this.backupInterval);
      this.backupInterval = null;
    }
  }

  private async validateAndRestore(): Promise<void> {
    try {
      console.log('🔍 Validating data integrity on startup...');
      
      const students = await largeDataStorage.getStudents();
      
      if (students.length === 0) {
        console.log('📦 No students found, checking for emergency backups...');
        await this.restoreFromEmergencyBackup();
      } else {
        console.log(`✅ Found ${students.length} students, data integrity OK`);
        // Create a fresh backup
        this.createEmergencyBackup();
      }
    } catch (error) {
      console.error('❌ Data validation failed, attempting recovery:', error);
      await this.restoreFromEmergencyBackup();
    }
  }

  private async createEmergencyBackup(): Promise<void> {
    try {
      const students = await largeDataStorage.getStudents();
      
      if (students.length > 0) {
        const backupKey = `nalc_emergency_backup_${Date.now()}`;
        const backupData = {
          timestamp: new Date().toISOString(),
          studentCount: students.length,
          data: students
        };
        
        localStorage.setItem(backupKey, JSON.stringify(backupData));
        
        // Clean old backups
        this.cleanOldBackups();
        
        console.log(`🛡️ Emergency backup created: ${students.length} students`);
      }
    } catch (error) {
      console.error('❌ Emergency backup failed:', error);
    }
  }

  private cleanOldBackups(): void {
    try {
      const backupKeys = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('nalc_emergency_backup_')) {
          backupKeys.push(key);
        }
      }
      
      // Sort by timestamp (newest first)
      backupKeys.sort().reverse();
      
      // Remove old backups beyond the limit
      if (backupKeys.length > this.MAX_BACKUPS) {
        const toRemove = backupKeys.slice(this.MAX_BACKUPS);
        toRemove.forEach(key => localStorage.removeItem(key));
        console.log(`🧹 Cleaned ${toRemove.length} old backups`);
      }
    } catch (error) {
      console.error('❌ Backup cleanup failed:', error);
    }
  }

  private async restoreFromEmergencyBackup(): Promise<void> {
    try {
      console.log('🔄 Attempting to restore from emergency backup...');
      
      const backupKeys = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('nalc_emergency_backup_')) {
          backupKeys.push(key);
        }
      }
      
      if (backupKeys.length === 0) {
        console.log('❌ No emergency backups found');
        return;
      }
      
      // Get the most recent backup
      const latestBackupKey = backupKeys.sort().reverse()[0];
      const backupData = localStorage.getItem(latestBackupKey);
      
      if (backupData) {
        const backup = JSON.parse(backupData);
        await largeDataStorage.saveStudents(backup.data);
        console.log(`✅ Restored ${backup.studentCount} students from emergency backup`);
      }
    } catch (error) {
      console.error('❌ Emergency restore failed:', error);
    }
  }

  async getBackupInfo(): Promise<{
    totalBackups: number;
    lastBackup: string;
    totalStudentsInLatest: number;
  }> {
    try {
      const backupKeys = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('nalc_emergency_backup_')) {
          backupKeys.push(key);
        }
      }
      
      if (backupKeys.length === 0) {
        return {
          totalBackups: 0,
          lastBackup: 'Never',
          totalStudentsInLatest: 0
        };
      }
      
      const latestBackupKey = backupKeys.sort().reverse()[0];
      const backupData = localStorage.getItem(latestBackupKey);
      const backup = backupData ? JSON.parse(backupData) : null;
      
      return {
        totalBackups: backupKeys.length,
        lastBackup: backup?.timestamp || 'Unknown',
        totalStudentsInLatest: backup?.studentCount || 0
      };
    } catch (error) {
      console.error('Error getting backup info:', error);
      return {
        totalBackups: 0,
        lastBackup: 'Error',
        totalStudentsInLatest: 0
      };
    }
  }
}

export const autoBackupService = new AutoBackupService();
